/**
 * One audience: its name, budget share, client-facing description and
 * targeting. Dayparts, conquesting and an own unit mix are rarer, so they sit
 * under "More targeting" (open from the start when the audience uses them).
 */

import React, { useState } from 'react';
import { Button, Switch } from '../../../ui';
import FilterChipGroup from '../../FilterChipGroup';
import ChipInput from '../ChipInput';
import UnitMixEditor from '../UnitMixEditor';
import type { GeoScope, MediaPlanUnitDef } from '../types';
import { fieldId, parseGeoValues, segmentBudgetUsd, segmentField, type SegmentDraft } from '../planDraft';
import { CULTURE_STACKS, DAYPART_OPTIONS, DEMO_SUGGESTIONS, GEO_SCOPE_LABELS } from '../vocab';
import { fieldStyle, fmtUsd, hintStyle, labelStyle, numberFieldStyle } from '../formStyles';
import { FieldError, Optional, fieldAria, type ErrorFor } from './fields';

/** Suggestions shown under a chip field; the rest come up as the seller types. */
export const CHIP_SUGGESTIONS = 6;

interface Props {
  segment: SegmentDraft;
  index: number;
  count: number;
  budgetUsd: string;
  /** Effective share for the "≈ $X" hint (null when the shares don't add up yet). */
  sharePct: number | null;
  units: MediaPlanUnitDef[];
  onChange: (patch: Partial<SegmentDraft>) => void;
  onRemove: () => void;
  errorFor: ErrorFor;
  disabled?: boolean;
}

const GEO_OPTIONS: Array<{ value: GeoScope; label: string }> = (['national', 'dma', 'state', 'zip'] as GeoScope[]).map((v) => ({ value: v, label: GEO_SCOPE_LABELS[v] }));

const AudienceCard: React.FC<Props> = ({ segment, index, count, budgetUsd, sharePct, units, onChange, onRemove, errorFor, disabled = false }) => {
  const multi = count > 1;
  const name = segment.label.trim() || `Audience ${index + 1}`;
  const usesMore = segment.dayparts.length > 0 || segment.competitiveConquesting.length > 0 || segment.useOwnMix;
  const [moreOpen, setMoreOpen] = useState(usesMore);
  const approx = segmentBudgetUsd(budgetUsd, sharePct);
  const geoCount = segment.geoScope === 'national' ? 0 : parseGeoValues(segment.geoValuesText).length;
  const f = {
    label: segmentField(segment.id, 'label'),
    share: segmentField(segment.id, 'share'),
    geo: segmentField(segment.id, 'geo'),
    mix: segmentField(segment.id, 'mix'),
  };
  const labelError = errorFor(f.label);
  const shareError = errorFor(f.share);
  const geoError = errorFor(f.geo);
  const moreId = `${fieldId(`segment-${segment.id}`)}-more`;

  return (
    <div role="group" aria-label={name} style={styles.card}>
      <div style={styles.head}>
        <div style={{ display: 'grid', gridTemplateColumns: multi ? 'minmax(0, 1fr) 130px' : 'minmax(0, 1fr)', gap: 10, flex: 1 }}>
          <div>
            <label htmlFor={fieldId(f.label)} style={labelStyle}>
              {multi ? `Audience ${index + 1}` : 'Audience'}
            </label>
            <input
              id={fieldId(f.label)}
              style={fieldStyle}
              value={segment.label}
              maxLength={60}
              placeholder="General Market"
              disabled={disabled}
              onChange={(e) => onChange({ label: e.target.value })}
              {...fieldAria(f.label, labelError)}
            />
            <FieldError field={f.label} message={labelError} />
          </div>
          {multi && (
            <div>
              <label htmlFor={fieldId(f.share)} style={labelStyle}>
                Budget share
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id={fieldId(f.share)}
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  inputMode="decimal"
                  style={{ ...numberFieldStyle, paddingRight: 26 }}
                  value={segment.budgetPct}
                  placeholder="equal"
                  disabled={disabled}
                  onChange={(e) => onChange({ budgetPct: e.target.value })}
                  {...fieldAria(f.share, shareError)}
                />
                <span style={styles.pct}>%</span>
              </div>
              <FieldError field={f.share} message={shareError} />
              {approx !== null && !shareError && <div style={hintStyle}>≈ {fmtUsd(approx, 0)}</div>}
            </div>
          )}
        </div>
        {multi && (
          <Button variant="ghost" size="sm" onClick={onRemove} disabled={disabled} aria-label={`Remove ${name}`}>
            Remove
          </Button>
        )}
      </div>

      <div>
        <label htmlFor={fieldId(`segment-${segment.id}-desc`)} style={labelStyle}>
          Description for the client <Optional />
        </label>
        <input
          id={fieldId(`segment-${segment.id}-desc`)}
          style={fieldStyle}
          value={segment.description}
          maxLength={200}
          placeholder="Blank = described from the targeting below"
          disabled={disabled}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </div>

      <div style={styles.grid}>
        <ChipInput
          label="Demo"
          values={segment.demo}
          onChange={(demo) => onChange({ demo })}
          suggestions={DEMO_SUGGESTIONS}
          maxSuggestions={CHIP_SUGGESTIONS}
          placeholder="A25-54, AACM, Hispanic A18-49…"
          hint="Age-only demos (A25-54) carry no demo upcharge."
          disabled={disabled}
        />
        <ChipInput
          label="Culture Stack interests"
          values={segment.cultureStacks}
          onChange={(cultureStacks) => onChange({ cultureStacks })}
          suggestions={CULTURE_STACKS}
          maxSuggestions={CHIP_SUGGESTIONS}
          placeholder="Music, Latino Culture, QSR…"
          disabled={disabled}
        />
      </div>

      <div>
        <div style={labelStyle}>Geography</div>
        <FilterChipGroup options={GEO_OPTIONS} value={segment.geoScope} onChange={(geoScope) => onChange({ geoScope })} size="sm" />
        {segment.geoScope !== 'national' && (
          <>
            <textarea
              id={fieldId(f.geo)}
              style={{ ...fieldStyle, marginTop: 8, minHeight: 72, resize: 'vertical' }}
              value={segment.geoValuesText}
              placeholder={segment.geoScope === 'dma' ? 'Los Angeles, Houston, Miami-Ft. Lauderdale…' : segment.geoScope === 'state' ? 'CA, TX, FL…' : '90001, 77002…'}
              aria-label={`${GEO_SCOPE_LABELS[segment.geoScope]} for ${name}`}
              disabled={disabled}
              onChange={(e) => onChange({ geoValuesText: e.target.value })}
              {...fieldAria(f.geo, geoError)}
            />
            <FieldError field={f.geo} message={geoError} />
            <div style={hintStyle}>
              {geoCount} {GEO_SCOPE_LABELS[segment.geoScope].toLowerCase()} · comma or line separated
            </div>
          </>
        )}
      </div>

      <div>
        <button type="button" aria-expanded={moreOpen} aria-controls={moreId} onClick={() => setMoreOpen((o) => !o)} style={styles.disclosure}>
          {moreOpen ? '▾' : '▸'} More targeting
          {!moreOpen && usesMore && <span style={{ ...hintStyle, marginTop: 0, marginLeft: 6 }}>in use</span>}
        </button>
        {moreOpen && (
          <div id={moreId} style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 10 }}>
            <div style={styles.grid}>
              <ChipInput
                label="Dayparts"
                values={segment.dayparts}
                onChange={(dayparts) => onChange({ dayparts })}
                suggestions={DAYPART_OPTIONS}
                maxSuggestions={CHIP_SUGGESTIONS}
                placeholder="Primetime 7–11pm…"
                disabled={disabled}
              />
              <ChipInput
                label="Competitive conquesting"
                values={segment.competitiveConquesting}
                onChange={(competitiveConquesting) => onChange({ competitiveConquesting })}
                placeholder="Competitor brands…"
                disabled={disabled}
              />
            </div>
            <Switch
              size="sm"
              checked={segment.useOwnMix}
              onChange={(useOwnMix) => onChange({ useOwnMix })}
              label="Own unit mix"
              description={`Split ${name}'s budget across ad units differently from the plan.`}
              disabled={disabled}
            />
            {segment.useOwnMix && (
              <>
                <div id={fieldId(f.mix)} tabIndex={-1} style={{ outline: 'none' }}>
                  <UnitMixEditor units={units} mix={segment.unitMix} onChange={(unitMix) => onChange({ unitMix })} disabled={disabled} compact />
                </div>
                <FieldError field={f.mix} message={errorFor(f.mix)} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: 16,
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-surface-2)',
  },
  head: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 },
  pct: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--fg-4)', pointerEvents: 'none' },
  disclosure: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--fg-link)',
  },
};

export default AudienceCard;
